import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SUBMENU_EXPANDED_BODY_CLASS = 'has-expanded-submenu';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = [];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #deactivateTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointerover', this.#megaMenuInteractionListener);
    this.addEventListener('focusin', this.#megaMenuInteractionListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointerover', this.#megaMenuInteractionListener);
    this.removeEventListener('focusin', this.#megaMenuInteractionListener);
    this.#cleanupMutationObserver();
    this.#setSubmenuExpandedBodyState(false);
    if (this.#deactivateTimeout !== null) {
      clearTimeout(this.#deactivateTimeout);
      this.#deactivateTimeout = null;
    }
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#deactivateTimeout !== null) {
      clearTimeout(this.#deactivateTimeout);
      this.#deactivateTimeout = null;
    }
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';

      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
      }
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Ensure the current lead image has a matching active link when a submenu opens.
      this.#syncActiveMegaMenuTextFromDisplayedImages(submenu);

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const submenuHasMegaMenu = submenu instanceof Element && Boolean(submenu.querySelector('.mega-menu'));

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#setSubmenuExpandedBodyState(finalHeight > 0 && submenuHasMegaMenu);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivateTimeout = setTimeout(() => {
      this.#deactivateTimeout = null;
      this.#deactivate();
    }, 250);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    if (submenu) {
      this.#resetMegaMenuColumnImages(submenu);
      this.#clearActiveMegaMenuText(submenu);
    }

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }

    this.#setSubmenuExpandedBodyState(false);
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Handle mega-menu link hover/focus interactions.
   * @param {Event} event
   */
  #megaMenuInteractionListener = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('a.mega-menu__link');
    if (!(link instanceof HTMLAnchorElement)) return;

    const menuList = link.closest('[data-menu-list-id]');
    if (!(menuList instanceof HTMLElement)) return;

    const replacement = link.dataset.imageReplacement?.trim();
    if (!replacement) return;

    // Only move the active text marker when this link is actually driving the lead image.
    const didDisplayReplacement = this.#replaceMegaMenuColumnImage(menuList, replacement);
    if (didDisplayReplacement) {
      this.#setActiveMegaMenuText(link, menuList);
    }
  };

  /**
   * @param {HTMLAnchorElement} activeLink
   * @param {ParentNode} root
   */
  #setActiveMegaMenuText(activeLink, root) {
    this.#clearActiveMegaMenuText(root);

    const activeText = activeLink.querySelector('.wrap-text');
    if (!(activeText instanceof HTMLElement)) return;

    activeText.classList.add('is-active');
  }

  /**
   * @param {ParentNode} root
   */
  #clearActiveMegaMenuText(root) {
    const activeWrapTexts = root.querySelectorAll('a.mega-menu__link .wrap-text.is-active');
    activeWrapTexts.forEach((wrapText) => wrapText.classList.remove('is-active'));
  }

  /**
   * @param {HTMLElement} menuList
   * @param {string} replacement
   * @returns {boolean}
   */
  #replaceMegaMenuColumnImage(menuList, replacement) {
    const image = menuList.querySelector('.mega-menu__column--image .mega-menu__link-image');
    if (!(image instanceof HTMLImageElement)) return false;

    if (!image.dataset.defaultSrc) {
      image.dataset.defaultSrc = image.getAttribute('src') ?? '';
      image.dataset.defaultSrcset = image.getAttribute('srcset') ?? '';
    }

    if (image.dataset.currentReplacement === replacement) return true;

    image.src = replacement;
    image.srcset = replacement;
    image.dataset.currentReplacement = replacement;

    return true;
  }

  /**
   * @param {ParentNode} root
   */
  #resetMegaMenuColumnImages(root) {
    const images = root.querySelectorAll('.mega-menu__column--image .mega-menu__link-image');

    images.forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;

      if (image.dataset.defaultSrc) {
        image.src = image.dataset.defaultSrc;
      }

      if (image.dataset.defaultSrcset) {
        image.srcset = image.dataset.defaultSrcset;
      } else {
        image.removeAttribute('srcset');
      }

      delete image.dataset.currentReplacement;
    });
  }

  /**
   * Aligns .wrap-text.is-active with the currently displayed lead image in each mega menu list.
   * @param {ParentNode} root
   */
  #syncActiveMegaMenuTextFromDisplayedImages(root) {
    const menuLists = root.querySelectorAll('[data-menu-list-id]');

    menuLists.forEach((menuList) => {
      if (!(menuList instanceof HTMLElement)) return;

      const image = menuList.querySelector('.mega-menu__column--image .mega-menu__link-image');
      if (!(image instanceof HTMLImageElement)) return;

      const displayedImage = normalizeUrl(image.currentSrc || image.getAttribute('src') || '');
      if (!displayedImage) return;

      this.#clearActiveMegaMenuText(menuList);

      const links = menuList.querySelectorAll('a.mega-menu__link[data-image-replacement]');
      for (const link of links) {
        if (!(link instanceof HTMLAnchorElement)) continue;

        const replacement = normalizeUrl(link.dataset.imageReplacement?.trim() || '');
        if (!replacement || replacement !== displayedImage) continue;

        this.#setActiveMegaMenuText(link, menuList);
        break;
      }
    });
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * @param {boolean} expanded
   */
  #setSubmenuExpandedBodyState(expanded) {
    const body = document.body;
    if (!body) return;

    if (expanded) {
      body.classList.add(SUBMENU_EXPANDED_BODY_CLASS);
      return;
    }

    const anyExpandedMegaMenu = Boolean(document.querySelector('.menu-list__submenu[data-active] .mega-menu'));
    body.classList.toggle(SUBMENU_EXPANDED_BODY_CLASS, anyExpandedMegaMenu);
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Normalize URL-like values so absolute and relative sources can be compared reliably.
 * @param {string} value
 * @returns {string}
 */
function normalizeUrl(value) {
  if (!value) return '';

  try {
    const url = new URL(value, window.location.origin);
    return `${url.pathname}${url.search}`;
  } catch {
    return value;
  }
}

